import { useState, useRef, useEffect } from 'react'
import { useTheme } from 'styled-components'
import useListData from '../../hooks/useListData'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import useToast from '../../hooks/useToast'
import { toGuardMessage } from '../../utils/guardMessage'
import useCanvasGestures from './useCanvasGestures'
import {
    Wrap, Toolbar, ToolBtn, Btn, DangerBtn,
    ZoomControl, ZoomBtn, ZoomLabel, SelectionPanel, PanelLabel, Hint,
} from './designerShell'
import {
    Icon_Pointer, Icon_Move, Icon_Route, Icon_Rectangle,
    Icon_Fit, Icon_Reset, Icon_Sync, Icon_CheckMark
} from '../../styles/icons'

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------
function rotatePoint(p, c, deg) {
    const r = (deg * Math.PI) / 180
    const cos = Math.cos(r), sin = Math.sin(r)
    const dx = p.x - c.x, dy = p.y - c.y
    return { x: c.x + dx * cos - dy * sin, y: c.y + dx * sin + dy * cos }
}

function rackCorners(g) {
    const hw = g.width / 2, hh = g.height / 2
    const local = [
        { x: g.x - hw, y: g.y - hh },
        { x: g.x + hw, y: g.y - hh },
        { x: g.x + hw, y: g.y + hh },
        { x: g.x - hw, y: g.y + hh },
    ]
    return local.map(p => rotatePoint(p, { x: g.x, y: g.y }, g.rotation || 0))
}

function snapValue(v, tol = 50) {
    return Math.round(v / tol) * tol
}

// Work-center position geometry is stored as an OFFSET in the footprint rack's
// local frame (see spec 28.05 D2). These convert between that offset and the
// absolute world geometry used by the shared move/resize handles. The SVG render
// applies rotate(-rot), so world = rotatePoint(centre+offset, centre, -rot).
function posWorldGeom(off, rackG) {
    if (!off || !rackG) return null
    const c = { x: rackG.x, y: rackG.y }
    const w = rotatePoint({ x: rackG.x + (off.x || 0), y: rackG.y + (off.y || 0) }, c, -(rackG.rotation || 0))
    return { x: w.x, y: w.y, width: off.width, height: off.height, rotation: (rackG.rotation || 0) + (off.rotation || 0) }
}

function posLocalGeom(world, rackG) {
    const c = { x: rackG.x, y: rackG.y }
    const l = rotatePoint({ x: world.x, y: world.y }, c, (rackG.rotation || 0))
    return {
        x: snapValue(l.x - rackG.x),
        y: snapValue(l.y - rackG.y),
        width: snapValue(world.width),
        height: snapValue(world.height),
        rotation: Math.round((world.rotation || 0) - (rackG.rotation || 0)),
    }
}

// Constrain a point so its segment from `prev` is axis-aligned (H or V).
function orthoSnap(prev, pt) {
    const dx = pt.x - prev.x, dy = pt.y - prev.y
    if (Math.abs(dx) >= Math.abs(dy)) return { x: snapValue(pt.x), y: snapValue(prev.y) }
    return { x: snapValue(prev.x), y: snapValue(pt.y) }
}

function screenToMm(screenX, screenY, tx, ty, scale) {
    return { x: (screenX - tx) / scale, y: (screenY - ty) / scale }
}

// Distance from point p to segment a→b (for inserting a path breakpoint).
function pointSegDist(p, a, b) {
    const dx = b.x - a.x, dy = b.y - a.y
    const len2 = dx * dx + dy * dy || 1
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2
    t = Math.max(0, Math.min(1, t))
    const cx = a.x + t * dx, cy = a.y + t * dy
    return Math.hypot(p.x - cx, p.y - cy)
}

// Intersection point of segments p1→p2 and p3→p4, or null if they don't cross.
function segIntersect(p1, p2, p3, p4) {
    const d1x = p2.x - p1.x, d1y = p2.y - p1.y
    const d2x = p4.x - p3.x, d2y = p4.y - p3.y
    const den = d1x * d2y - d1y * d2x
    if (Math.abs(den) < 1e-6) return null
    const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / den
    const s = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / den
    if (t < 0 || t > 1 || s < 0 || s > 1) return null
    return { x: p1.x + t * d1x, y: p1.y + t * d1y }
}

// All points where segments of different paths cross.
function computeIntersections(pathList) {
    const segs = []
    pathList.forEach(p => {
        const vs = p.vertices || []
        for (let i = 0; i < vs.length - 1; i++) segs.push({ a: vs[i], b: vs[i + 1], pid: p.id })
    })
    const pts = []
    for (let i = 0; i < segs.length; i++) {
        for (let j = i + 1; j < segs.length; j++) {
            if (segs[i].pid === segs[j].pid) continue
            const ip = segIntersect(segs[i].a, segs[i].b, segs[j].a, segs[j].b)
            if (ip) pts.push(ip)
        }
    }
    return pts
}

function insertVertex(vertices, pt) {
    if (vertices.length < 2) return [...vertices, { x: snapValue(pt.x), y: snapValue(pt.y) }]
    let bestIdx = 0, bestDist = Infinity
    for (let i = 0; i < vertices.length - 1; i++) {
        const d = pointSegDist(pt, vertices[i], vertices[i + 1])
        if (d < bestDist) { bestDist = d; bestIdx = i }
    }
    const copy = vertices.slice()
    copy.splice(bestIdx + 1, 0, { x: snapValue(pt.x), y: snapValue(pt.y) })
    return copy
}

function computeBBox(halls, racks, paths) {
    const pts = []
    halls.forEach(h => { if (h.geometry) rackCorners(h.geometry).forEach(p => pts.push(p)) })
    racks.forEach(r => { if (r.geometry) rackCorners(r.geometry).forEach(p => pts.push(p)) })
    paths.forEach(pa => { (pa.vertices || []).forEach(v => pts.push(v)) })
    if (!pts.length) return null
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) }
}

// Auto-layout sizing (mm). Each rack position is roughly a EUR pallet footprint
// (1200 along the rack length × 800 deep). Racks are stood vertically (rotated)
// and lined up side by side inside their hall.
const PALLET_LEN = 800    // per-position extent along the rack length (pallet width facing the aisle)
const RACK_DEPTH = 1200   // rack depth (pallet inserted deep)
const RACK_GAP = 1600     // aisle between adjacent racks
const HALL_PAD = 1800     // padding inside a hall
const HALL_GAP = 3000     // gap between halls
const MIN_HALL_W = 8000
const MIN_HALL_H = 6000

const OBJECT_COLOR = '#64748b'  // slate — physical no-access objects
const POSITION_COLOR = '#0ea5e9' // sky — work-center positions

// Raw scale (px/mm) shown as 100% in the zoom label. Chosen so a typical
// full-warehouse fit (~0.04 px/mm) reads ~20%.
const ZOOM_DISPLAY_REF = 0.2

function orphanRackGeometry(index) {
    const cols = 6
    const col = index % cols
    const row = Math.floor(index / cols)
    return { x: -22000 + col * 1400, y: -8000 + row * (RACK_DEPTH + 600), width: PALLET_LEN, height: RACK_DEPTH, rotation: 0 }
}

// On-screen footprint of a vertical (rotated 90°) rack.
function rackFootprint(rack) {
    const n = rack.totalPositions || 1
    return { w: RACK_DEPTH, h: n * PALLET_LEN }
}

// Build display geometry for everything. Halls are laid out left-to-right; racks
// are stood vertically and lined up inside the hall they belong to (ordered by
// code); the hall grows to fit; sub-areas are dropped inside the first hall.
function computeLayout(halls, racks, objects = []) {
    const enriched = halls.map(h => ({
        ...h,
        _isArea: !!h.isArea,
        _placed: !!(h.geometry?.width && h.geometry?.height),
        geometry: (h.geometry?.width && h.geometry?.height) ? { ...h.geometry } : null,
        _unplaced: !(h.geometry?.width && h.geometry?.height),
    }))
    const enrichedObjects = objects.map(o => ({
        ...o,
        _isObject: true,
        _placed: !!(o.geometry?.width && o.geometry?.height),
        geometry: (o.geometry?.width && o.geometry?.height) ? { ...o.geometry } : null,
        _unplaced: !(o.geometry?.width && o.geometry?.height),
    }))
    const containerHalls = enriched.filter(h => !h._isArea)
    const overlayHalls = [...enriched.filter(h => h._isArea), ...enrichedObjects]  // areas + objects
    const hallById = Object.fromEntries(containerHalls.map(h => [h.id, h]))

    const outRacks = []
    const unplacedByHall = {}
    const orphans = []
    racks.forEach(r => {
        if (r.geometry?.width && r.geometry?.height) { outRacks.push(r); return }
        if (r.hallId && hallById[r.hallId]) (unplacedByHall[r.hallId] ||= []).push(r)
        else orphans.push(r)
    })
    const sortByCode = (a, b) => String(a.code || '').localeCompare(String(b.code || ''), undefined, { numeric: true })

    // Size each container hall to fit its row of vertical racks.
    containerHalls.forEach(h => {
        const list = (unplacedByHall[h.id] || []).sort(sortByCode)
        let racksW = 0, maxRackH = 0
        list.forEach(r => { const f = rackFootprint(r); racksW += f.w + RACK_GAP; maxRackH = Math.max(maxRackH, f.h) })
        if (list.length) racksW -= RACK_GAP
        const neededW = Math.max(MIN_HALL_W, racksW + 2 * HALL_PAD)
        const neededH = Math.max(MIN_HALL_H, maxRackH + 2 * HALL_PAD)
        // Only auto-size unplaced halls; a placed hall's saved geometry is respected.
        if (!h._placed) {
            h.geometry = { x: 0, y: 0, width: snapValue(neededW), height: snapValue(neededH), rotation: 0 }
        }
    })

    // Position unplaced container halls horizontally next to each other.
    let cursorX = 0
    containerHalls.forEach(h => {
        if (h._placed) return
        h.geometry.x = cursorX + h.geometry.width / 2
        h.geometry.y = h.geometry.height / 2
        cursorX += h.geometry.width + HALL_GAP
    })

    // Lay racks out as a row of vertical racks inside their hall, ordered by code.
    containerHalls.forEach(h => {
        const list = (unplacedByHall[h.id] || []).sort(sortByCode)
        if (!list.length) return
        const g = h.geometry
        let cx = g.x - g.width / 2 + HALL_PAD
        const top = g.y - g.height / 2 + HALL_PAD
        list.forEach(r => {
            const n = r.totalPositions || 1
            const f = rackFootprint(r)
            outRacks.push({
                ...r,
                _unplaced: true,
                geometry: {
                    x: cx + f.w / 2,
                    y: top + f.h / 2,
                    width: n * PALLET_LEN,   // along the rack length (vertical after rotation)
                    height: RACK_DEPTH,      // depth
                    rotation: 90,
                },
            })
            cx += f.w + RACK_GAP
        })
    })

    // Drop overlays (sub-areas / objects) inside the first hall (row along the bottom).
    const firstHall = containerHalls.find(h => h.geometry)
    overlayHalls.forEach((a, i) => {
        if (a._placed) return
        if (firstHall) {
            const g = firstHall.geometry
            const aw = Math.min(g.width * 0.28, 4000)
            const ah = Math.min(g.height * 0.28, 3000)
            a.geometry = {
                x: g.x - g.width / 2 + HALL_PAD + aw / 2 + i * (aw + 1000),
                y: g.y + g.height / 2 - HALL_PAD - ah / 2,
                width: snapValue(aw), height: snapValue(ah), rotation: 0,
            }
        } else {
            a.geometry = { x: -7000, y: 4000 + i * 5000, width: 4000, height: 3000, rotation: 0 }
        }
    })

    orphans.forEach((r, i) => outRacks.push({ ...r, _unplaced: true, geometry: orphanRackGeometry(i) }))

    return { halls: enriched, racks: outRacks, objects: enrichedObjects }
}

// ---------------------------------------------------------------------------
// SVG sub-components
// ---------------------------------------------------------------------------
function HallRect({ hall, selected, unplaced, onClick }) {
    const theme = useTheme()
    const g = hall.geometry
    if (!g || !g.width || !g.height) return null
    const isArea = !!hall.isArea
    const isObject = !!hall._isObject
    const borderW = Math.max(40, Math.min(g.width, g.height) * 0.015)
    // Objects (no-access) are a solid hatched slate block; areas are a dashed
    // primary tint; halls are a translucent neutral fill.
    const fill = isObject ? 'url(#objectHatch)' : (hall.color || (isArea ? theme.btn.primary : theme.area.bg))
    const stroke = selected ? theme.btn.primary : isObject ? OBJECT_COLOR : isArea ? theme.btn.primary : theme.area.lightBorder
    const dashed = (selected || unplaced || isArea) && !isObject
    return (
        <g
            transform={`rotate(${-(g.rotation || 0)} ${g.x} ${g.y})`}
            style={{ cursor: 'move' }}
            onClick={e => { e.stopPropagation(); onClick(hall) }}
        >
            <rect
                x={g.x - g.width / 2}
                y={g.y - g.height / 2}
                width={g.width}
                height={g.height}
                fill={fill}
                fillOpacity={isObject ? 1 : isArea ? 0.16 : 0.5}
                stroke={stroke}
                strokeWidth={selected ? borderW * 1.6 : borderW}
                strokeDasharray={dashed ? `${borderW * 6} ${borderW * 3}` : undefined}
                strokeOpacity={unplaced ? 0.6 : 1}
            />
        </g>
    )
}

// Label drawn in a separate pass so it always sits above sibling shapes.
function HallLabel({ hall }) {
    const theme = useTheme()
    const g = hall.geometry
    if (!g || !g.width || !g.height) return null
    const isArea = !!hall.isArea
    const isObject = !!hall._isObject
    const borderW = Math.max(40, Math.min(g.width, g.height) * 0.015)
    const fontSize = Math.max(100, g.width * 0.06)
    return (
        <text
            transform={`rotate(${-(g.rotation || 0)} ${g.x} ${g.y})`}
            x={g.x - g.width / 2}
            y={g.y - g.height / 2 - borderW * 2}
            textAnchor="start"
            dominantBaseline="auto"
            fontSize={fontSize}
            fontWeight="600"
            fill={isObject ? OBJECT_COLOR : isArea ? theme.btn.primary : theme.font.label}
            stroke={theme.page.background}
            strokeWidth={fontSize * 0.18}
            paintOrder="stroke"
            strokeLinejoin="round"
            pointerEvents="none"
            style={{ userSelect: 'none' }}
        >
            {hall.name || hall.code}
        </text>
    )
}

function RackShape({ rack, selected, unplaced, onClick }) {
    const theme = useTheme()
    const g = rack.geometry
    if (!g || !g.width || !g.height) return null
    const positions = rack.totalPositions || 1
    const cellW = g.width / positions
    const posOriginEnd = rack.positionOrigin === 'END'
    const dividerW = Math.max(20, g.height * 0.03)

    return (
        <g
            transform={`rotate(${-(g.rotation || 0)} ${g.x} ${g.y})`}
            style={{ cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); onClick(rack) }}
        >
            {/* cells — one column per position; levels are vertical (not shown in 2D).
                Zebra fill + divider lines make individual positions clearly visible. */}
            {Array.from({ length: positions }).map((_, col) => (
                <rect
                    key={col}
                    x={g.x - g.width / 2 + col * cellW}
                    y={g.y - g.height / 2}
                    width={cellW}
                    height={g.height}
                    fill={col % 2 === 0 ? theme.card.background : theme.area.bg}
                />
            ))}
            {/* divider lines between positions */}
            {Array.from({ length: Math.max(0, positions - 1) }).map((_, i) => {
                const lx = g.x - g.width / 2 + (i + 1) * cellW
                return (
                    <line
                        key={`div-${i}`}
                        x1={lx}
                        y1={g.y - g.height / 2}
                        x2={lx}
                        y2={g.y + g.height / 2}
                        stroke={theme.area.border}
                        strokeWidth={dividerW}
                    />
                )
            })}
            {/* outer border */}
            <rect
                x={g.x - g.width / 2}
                y={g.y - g.height / 2}
                width={g.width}
                height={g.height}
                fill="none"
                stroke={selected ? theme.btn.primary : theme.area.border}
                strokeWidth={selected ? dividerW * 1.6 : dividerW}
                strokeDasharray={unplaced ? '200 100' : undefined}
                strokeOpacity={unplaced ? 0.6 : 1}
            />
            {/* start (1) and end (N) position markers — centered in their cells */}
            {(() => {
                const leftX = g.x - g.width / 2 + cellW / 2
                const rightX = g.x + g.width / 2 - cellW / 2
                const startX = posOriginEnd ? rightX : leftX   // position 1
                const endX = posOriginEnd ? leftX : rightX     // position N
                const fontSize = Math.max(60, Math.min(cellW, g.height) * 0.35)
                const markers = [{ x: startX, label: '1' }]
                if (positions > 1) markers.push({ x: endX, label: String(positions) })
                return markers.map((m, i) => (
                    <text
                        key={i}
                        transform={`rotate(${g.rotation || 0} ${m.x} ${g.y})`}
                        x={m.x}
                        y={g.y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={fontSize}
                        fontWeight="600"
                        fill={theme.btn.primary}
                        pointerEvents="none"
                    >{m.label}</text>
                ))
            })()}
            {/* rack code — rendered inside a pill for readability */}
            {(() => {
                const code = rack.code || ''
                const fontSize = Math.max(60, Math.min(g.width, g.height) * 0.2)
                const pillW = code.length * fontSize * 0.62 + fontSize
                const pillH = fontSize * 1.6
                return (
                    <g pointerEvents="none" transform={`rotate(${g.rotation || 0} ${g.x} ${g.y})`}>
                        <rect
                            x={g.x - pillW / 2}
                            y={g.y - pillH / 2}
                            width={pillW}
                            height={pillH}
                            rx={pillH / 2}
                            ry={pillH / 2}
                            fill={theme.card.background}
                            fillOpacity={0.95}
                            stroke={selected ? theme.btn.primary : theme.area.border}
                            strokeWidth={Math.max(8, fontSize * 0.06)}
                        />
                        <text
                            x={g.x}
                            y={g.y}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={fontSize}
                            fontWeight="600"
                            fill={theme.font.color}
                            style={{ userSelect: 'none' }}
                        >
                            {code}
                        </text>
                    </g>
                )
            })()}
        </g>
    )
}

// One work-center position rectangle. `cx,cy` is the position centre in the
// coordinate frame of the enclosing group (inside the footprint's rotate(-rot)
// group when embedded, or world space when the position itself is selected).
function PositionRect({ cx, cy, w, h, rot = 0, label, selected, onPointerDown, onClick }) {
    const theme = useTheme()
    if (!w || !h) return null
    const strokeW = Math.max(10, Math.min(w, h) * 0.06)
    const font = Math.max(50, Math.min(w, h) * 0.32)
    return (
        <g transform={`rotate(${-rot} ${cx} ${cy})`} style={{ cursor: 'move' }} onPointerDown={onPointerDown} onClick={onClick}>
            <rect
                x={cx - w / 2}
                y={cy - h / 2}
                width={w}
                height={h}
                rx={strokeW}
                fill={POSITION_COLOR}
                fillOpacity={selected ? 0.38 : 0.22}
                stroke={selected ? theme.btn.primary : POSITION_COLOR}
                strokeWidth={selected ? strokeW * 1.5 : strokeW}
            />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={font} fontWeight="700" fill={theme.font.color} pointerEvents="none">
                {label}
            </text>
        </g>
    )
}

// A work-center rack is a footprint (outer block) with each position drawn as a
// small rectangle in the rack's local frame. Positions move/rotate with the
// footprint because they render inside the same rotate(-rot) group. The selected
// position is skipped here — it is drawn in world space (with handles) by the
// caller so the shared move/resize machinery applies.
function WorkCenterShape({ rack, positions, selectedPositionId, selected, onFootprintClick, onPositionDown }) {
    const theme = useTheme()
    const g = rack.geometry
    if (!g || !g.width || !g.height) return null
    const rot = g.rotation || 0
    const border = Math.max(20, Math.min(g.width, g.height) * 0.02)
    const code = rack.code || rack.name || ''
    const codeFont = Math.max(60, Math.min(g.width, g.height) * 0.14)
    const pillH = codeFont * 1.6
    const pillW = code.length * codeFont * 0.62 + codeFont
    return (
        <g
            transform={`rotate(${-rot} ${g.x} ${g.y})`}
            style={{ cursor: 'move' }}
            onClick={e => { e.stopPropagation(); onFootprintClick(rack) }}
        >
            <rect
                x={g.x - g.width / 2}
                y={g.y - g.height / 2}
                width={g.width}
                height={g.height}
                rx={border}
                fill={theme.area.bg}
                fillOpacity={0.55}
                stroke={selected ? theme.btn.primary : theme.area.border}
                strokeWidth={selected ? border * 1.6 : border}
                strokeDasharray={rack._unplaced ? '200 100' : `${border * 5} ${border * 3}`}
                strokeOpacity={rack._unplaced ? 0.6 : 1}
            />
            {/* code pill, top-left */}
            <g pointerEvents="none">
                <rect
                    x={g.x - g.width / 2 + border}
                    y={g.y - g.height / 2 + border}
                    width={pillW}
                    height={pillH}
                    rx={pillH / 2}
                    fill={theme.card.background}
                    fillOpacity={0.95}
                    stroke={theme.area.border}
                    strokeWidth={Math.max(6, codeFont * 0.05)}
                />
                <text x={g.x - g.width / 2 + border + pillW / 2} y={g.y - g.height / 2 + border + pillH / 2} textAnchor="middle" dominantBaseline="middle" fontSize={codeFont} fontWeight="600" fill={theme.font.color}>
                    {code}
                </text>
            </g>
            {/* positions (offset in local frame) */}
            {positions.map(loc => {
                const off = loc.geometry
                if (!off || !off.width || selectedPositionId === loc.id) return null
                return (
                    <PositionRect
                        key={loc.id}
                        cx={g.x + (off.x || 0)}
                        cy={g.y + (off.y || 0)}
                        w={off.width}
                        h={off.height}
                        rot={off.rotation || 0}
                        label={loc.position}
                        onPointerDown={e => onPositionDown(e, loc, rack)}
                        onClick={e => e.stopPropagation()}
                    />
                )
            })}
        </g>
    )
}

function PathLine({ path, selected, onClick }) {
    const theme = useTheme()
    const vs = path.vertices || []
    if (vs.length < 2) return null
    const pts = vs.map(v => `${v.x},${v.y}`).join(' ')
    return (
        <polyline
            points={pts}
            fill="none"
            stroke={selected ? theme.btn.primary : theme.area.border}
            strokeWidth={(path.width || 600) * 0.15}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ cursor: 'pointer' }}
        />
    )
}

// Vertex handles for editing a selected path. Sizes are screen-constant (u = 1px).
function PathHandles({ path, scale, onVertexDown, onSegmentDown }) {
    const theme = useTheme()
    const vs = path.vertices || []
    const u = 1 / scale
    return (
        <>
            {/* segment grips — drag to slide a segment perpendicular to itself */}
            {vs.slice(0, -1).map((a, i) => {
                const b = vs[i + 1]
                return (
                    <line
                        key={`seg-${i}`}
                        x1={a.x}
                        y1={a.y}
                        x2={b.x}
                        y2={b.y}
                        stroke="transparent"
                        strokeWidth={14 * u}
                        strokeLinecap="round"
                        style={{ cursor: 'move' }}
                        onPointerDown={e => onSegmentDown(e, i)}
                    />
                )
            })}
            {vs.map((v, i) => {
                const isStart = i === 0
                const isEnd = i === vs.length - 1
                const fill = isStart ? '#22c55e' : isEnd ? '#ef4444' : theme.btn.primary
                return (
                    <circle
                        key={i}
                        cx={v.x}
                        cy={v.y}
                        r={7 * u}
                        fill={fill}
                        stroke={theme.card.background}
                        strokeWidth={2 * u}
                        style={{ cursor: 'grab' }}
                        onPointerDown={e => onVertexDown(e, i)}
                    />
                )
            })}
        </>
    )
}

// Nodes shown where paths cross each other.
function JunctionNodes({ points, scale }) {
    const theme = useTheme()
    if (!points.length) return null
    const u = 1 / scale
    return (
        <>
            {points.map((p, i) => (
                <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={6 * u}
                    fill={theme.btn.primary}
                    stroke={theme.card.background}
                    strokeWidth={2 * u}
                    pointerEvents="none"
                />
            ))}
        </>
    )
}

function StopCircles({ stops }) {
    const theme = useTheme()
    if (!stops || !stops.length) return null
    // Rack-position stops are small green/red dots; zone anchors (shipping /
    // receiving / transfer) are larger labelled blue markers on the path.
    const rackStops = stops.filter(s => !s.zone)
    const zoneStops = stops.filter(s => s.zone)
    return (
        <>
            {rackStops.map((s, i) => (
                <circle
                    key={`r${i}`}
                    cx={s.x}
                    cy={s.y}
                    r={50}
                    fill={s.reachable === false ? '#ef4444' : '#22c55e'}
                    fillOpacity={0.75}
                    stroke={theme.card.background}
                    strokeWidth={15}
                    pointerEvents="none"
                />
            ))}
            {zoneStops.map((s, i) => (
                <g key={`z${i}`} pointerEvents="none">
                    <circle
                        cx={s.x}
                        cy={s.y}
                        r={130}
                        fill={s.reachable === false ? '#ef4444' : '#3b82f6'}
                        fillOpacity={0.9}
                        stroke={theme.card.background}
                        strokeWidth={25}
                    />
                    <text
                        x={s.x}
                        y={s.y - 210}
                        textAnchor="middle"
                        fontSize={280}
                        fontWeight="700"
                        fill={theme.font.color}
                        stroke={theme.card.background}
                        strokeWidth={50}
                        paintOrder="stroke"
                    >
                        {s.name || s.zone}
                    </text>
                </g>
            ))}
        </>
    )
}

// Pick the resize cursor that matches a handle's ON-SCREEN orientation, given
// the shape's rotation (render applies rotate(-θ)). Without this the cursor
// arrows stay axis-aligned and look wrong on rotated shapes.
function resizeCursor(vx, vy, rotationDeg) {
    const rad = -rotationDeg * Math.PI / 180
    const cos = Math.cos(rad), sin = Math.sin(rad)
    const sx = vx * cos - vy * sin
    const sy = vx * sin + vy * cos
    let a = Math.atan2(sy, sx) * 180 / Math.PI
    a = ((a % 180) + 180) % 180
    if (a < 22.5 || a >= 157.5) return 'ew-resize'
    if (a < 67.5) return 'nwse-resize'
    if (a < 112.5) return 'ns-resize'
    return 'nesw-resize'
}

// Move/resize/rotate handles for racks, halls and areas. Screen-constant sizing.
function SelectionHandles({ g, scale, onDragStart }) {
    const theme = useTheme()
    if (!g || !g.width || !g.height) return null
    const hw = g.width / 2, hh = g.height / 2
    const u = 1 / scale
    const rot = g.rotation || 0
    const corners = [
        { cx: g.x - hw, cy: g.y - hh, vx: -1, vy: -1, dir: 'nw' },
        { cx: g.x,      cy: g.y - hh, vx: 0,  vy: -1, dir: 'n'  },
        { cx: g.x + hw, cy: g.y - hh, vx: 1,  vy: -1, dir: 'ne' },
        { cx: g.x + hw, cy: g.y,      vx: 1,  vy: 0,  dir: 'e'  },
        { cx: g.x + hw, cy: g.y + hh, vx: 1,  vy: 1,  dir: 'se' },
        { cx: g.x,      cy: g.y + hh, vx: 0,  vy: 1,  dir: 's'  },
        { cx: g.x - hw, cy: g.y + hh, vx: -1, vy: 1,  dir: 'sw' },
        { cx: g.x - hw, cy: g.y,      vx: -1, vy: 0,  dir: 'w'  },
    ]
    const hs = 13 * u
    const hit = 26 * u  // larger invisible target so handles are easy to grab
    return (
        <g transform={`rotate(${-rot} ${g.x} ${g.y})`}>
            {corners.map(h => (
                <g key={h.dir} style={{ cursor: resizeCursor(h.vx, h.vy, rot) }} onPointerDown={e => { e.stopPropagation(); onDragStart(e, h.dir) }}>
                    <rect
                        x={h.cx - hit / 2}
                        y={h.cy - hit / 2}
                        width={hit}
                        height={hit}
                        fill="transparent"
                    />
                    <rect
                        x={h.cx - hs / 2}
                        y={h.cy - hs / 2}
                        width={hs}
                        height={hs}
                        fill={theme.card.background}
                        stroke={theme.btn.primary}
                        strokeWidth={2 * u}
                    />
                </g>
            ))}
            <line x1={g.x} y1={g.y - hh} x2={g.x} y2={g.y - hh - 28 * u} stroke={theme.btn.primary} strokeWidth={2 * u} />
            <circle
                cx={g.x}
                cy={g.y - hh - 28 * u}
                r={9 * u}
                fill={theme.btn.primary}
                style={{ cursor: 'grab' }}
                onPointerDown={e => { e.stopPropagation(); onDragStart(e, 'rotate') }}
            />
        </g>
    )
}

// ---------------------------------------------------------------------------
// Main designer
// ---------------------------------------------------------------------------
const SNAP = 50

function WarehouseLayoutDesigner({ warehouseId, warehouseCode }) {
    const theme = useTheme()
    const { t } = useTranslation()
    const { getMe, putMe, postMe, deleteMe } = useApi()
    const { showToast, ToastComponent } = useToast()
    const svgRef = useRef(null)
    const wrapRef = useRef(null)
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

    useEffect(() => {
        // The page layout chain (<main> → PageContent → AppArea) provides no
        // definite height, so `height: 100%` collapses and the SVG falls back to
        // its intrinsic 150px. Instead, measure the wrap's offset from the top of
        // the viewport and claim everything down to the bottom of the screen.
        function measure() {
            const el = wrapRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const height = Math.max(200, Math.floor(window.innerHeight - rect.top))
            setCanvasSize({ width: Math.round(rect.width), height })
        }
        measure()
        window.addEventListener('resize', measure)
        const obs = new ResizeObserver(measure)
        if (wrapRef.current) obs.observe(wrapRef.current)
        return () => {
            window.removeEventListener('resize', measure)
            obs.disconnect()
        }
    }, [])

    const [vp, setVp] = useState({ scale: 0.08, tx: 80, ty: 80 })
    const [mode, setMode] = useState('select')

    const [selected, setSelected] = useState(null)
    const dragRef = useRef(null)

    const [drawVerts, setDrawVerts] = useState([])
    const [cursorMm, setCursorMm] = useState(null)

    const [objectDraft, setObjectDraft] = useState(null)
    const objectDrawRef = useRef(null)

    const { data: hallPages, refetch: refetchHalls, isPending: hallsPending } = useListData({ endpoint: `warehouse/${warehouseId}/hall`, order: ['name', 'asc'] })
    const { data: rackPages, refetch: refetchRacks, isPending: racksPending } = useListData({ endpoint: 'rack', serverFilter: `warehouseId:${warehouseId}` })
    const { data: pathPages, refetch: refetchPaths, isPending: pathsPending } = useListData({ endpoint: 'warehousePath', serverFilter: `warehouseId:${warehouseId}` })
    const { data: objectPages, refetch: refetchObjects, isPending: objectsPending } = useListData({ endpoint: 'warehouseLayoutObject', serverFilter: `warehouseId:${warehouseId}` })
    // Work-center positions (drawn rectangles). Filtered at the DB level to
    // workCenter locations (a small set) so pagination can't hide them — a
    // post-fetch serverFilter would only see the first page of a big location
    // table. Narrowed to this warehouse client-side.
    const { data: locationPages, refetch: refetchLocations } = useListData({ endpoint: 'location', filterTerm: `type:workCenter` })
    const [graph, setGraph] = useState(null)
    const [rebuilding, setRebuilding] = useState(false)

    const halls = hallPages?.pages?.flatMap(p => p.data) || []
    const racks = rackPages?.pages?.flatMap(p => p.data) || []
    const paths = pathPages?.pages?.flatMap(p => p.data) || []
    const objects = objectPages?.pages?.flatMap(p => p.data) || []
    const wcLocations = (locationPages?.pages?.flatMap(p => p.data) || []).filter(l => l.warehouseId === warehouseId && l.geometry)
    const locsByRack = {}
    wcLocations.forEach(l => { (locsByRack[l.rackId] ||= []).push(l) })

    const layout = computeLayout(halls, racks, objects)
    const hallsWithGeometry = layout.halls.filter(h => !h.isArea)
    const areasWithGeometry = layout.halls.filter(h => h.isArea)
    const objectsWithGeometry = layout.objects
    const racksWithGeometry = layout.racks

    useEffect(() => {
        if (!warehouseId) return
        getMe(`warehouseRouteGraph/${warehouseId}`).then(r => setGraph(r?.data)).catch(() => {})
    }, [warehouseId])

    // Auto-fit once the canvas has real dimensions and there is something to show.
    // Bumping fitNonce (e.g. after Reset) forces a re-fit.
    //
    // The four geometry lists must all have arrived first. They resolve
    // independently, so fitting on the first one to land measures a fraction of
    // the drawing and leaves the rest off-screen — the halls are usually first
    // and the paths, which reach furthest, usually last.
    const geometryPending = hallsPending || racksPending || pathsPending || objectsPending
    const didFitRef = useRef(false)
    const [fitNonce, setFitNonce] = useState(0)
    useEffect(() => {
        if (didFitRef.current) return
        if (geometryPending) return
        if (!canvasSize.width || !canvasSize.height) return
        if (!layout.halls.length && !racksWithGeometry.length && !paths.length) return
        zoomToFit()
        didFitRef.current = true
    }, [canvasSize, geometryPending, layout.halls.length, racksWithGeometry.length, paths.length, fitNonce])

    function getSvgRect() {
        return svgRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 800, height: 600 }
    }

    function toMm(clientX, clientY) {
        const rect = getSvgRect()
        return screenToMm(clientX - rect.left, clientY - rect.top, vp.tx, vp.ty, vp.scale)
    }

    function zoomToFit() {
        const bbox = computeBBox([...layout.halls, ...objectsWithGeometry], racksWithGeometry, paths)
        if (!bbox) return
        const rect = getSvgRect()
        if (!rect.width || !rect.height) return
        const pad = 80
        const bboxW = bbox.maxX - bbox.minX || 1
        const bboxH = bbox.maxY - bbox.minY || 1
        const scaleX = (rect.width - pad * 2) / bboxW
        const scaleY = (rect.height - pad * 2) / bboxH
        const scale = Math.min(scaleX, scaleY)
        // Centre the content in the canvas (both axes), not just top-left padded.
        const tx = (rect.width - bboxW * scale) / 2 - bbox.minX * scale
        const ty = (rect.height - bboxH * scale) / 2 - bbox.minY * scale
        setVp({ scale, tx, ty })
    }

    // Wheel, the +/- buttons and pinch-to-zoom, shared with the site designer.
    const gestures = useCanvasGestures({
        svgRef, vp, setVp, minScale: 0.005, maxScale: 5,
        isBusy: () => !!dragRef.current || !!objectDrawRef.current,
        onPinchStart: () => endPan(),
    })
    const zoomBy = gestures.zoomBy

    const panRef = useRef(null)
    function startPan(e) {
        if (mode !== 'select' && mode !== 'pan') return
        if (mode === 'select' && selected) return  // let clicks select instead
        panRef.current = { startX: e.clientX, startY: e.clientY, tx: vp.tx, ty: vp.ty }
    }
    function doPan(e) {
        const p = panRef.current
        if (!p) return  // may be cleared between pointer events — read once, guard
        const { clientX, clientY } = e
        setVp(prev => ({
            ...prev,
            tx: p.tx + (clientX - p.startX),
            ty: p.ty + (clientY - p.startY),
        }))
    }
    function endPan() { panRef.current = null }

    function startDragHandle(e, dir) {
        if (!selected) return
        const mm = toMm(e.clientX, e.clientY)
        const g = selected.data.geometry || {}
        dragRef.current = { dir, startMmX: mm.x, startMmY: mm.y, origX: g.x || 0, origY: g.y || 0, origW: g.width || 1200, origH: g.height || 800, rotation: g.rotation || 0 }
    }

    function startVertexDrag(e, index) {
        e.stopPropagation()
        if (!selected || selected.type !== 'warehousePath') return
        // Alt+click removes a breakpoint (keep at least a start and end).
        if (e.altKey) {
            const verts = selected.data.vertices || []
            if (verts.length > 2) {
                const next = verts.filter((_, i) => i !== index)
                const data = { ...selected.data, vertices: next }
                setSelected(prev => ({ ...prev, data }))
                savePath(data)
            }
            return
        }
        dragRef.current = { dir: 'vertex', index }
    }

    function startSegmentDrag(e, index) {
        e.stopPropagation()
        if (!selected || selected.type !== 'warehousePath') return
        const vs = selected.data.vertices || []
        const a = vs[index], b = vs[index + 1]
        if (!a || !b) return
        let ux = b.x - a.x, uy = b.y - a.y
        const len = Math.hypot(ux, uy) || 1
        ux /= len; uy /= len
        const mm = toMm(e.clientX, e.clientY)
        dragRef.current = { dir: 'segment', index, ux, uy, startMmX: mm.x, startMmY: mm.y, origVerts: vs.map(v => ({ ...v })) }
    }

    function onPointerMove(e) {
        if (gestures.pointerMove(e)) return

        const mm = toMm(e.clientX, e.clientY)

        if (objectDrawRef.current) {
            const s = objectDrawRef.current
            setObjectDraft({ x0: s.x0, y0: s.y0, x1: mm.x, y1: mm.y })
            return
        }
        if (mode === 'addPath') {
            const last = drawVerts[drawVerts.length - 1]
            setCursorMm(last ? orthoSnap(last, mm) : { x: snapValue(mm.x), y: snapValue(mm.y) })
        }

        if (dragRef.current) {
            const d = dragRef.current
            if (d.dir === 'vertex') {
                setSelected(prev => {
                    const verts = (prev.data.vertices || []).slice()
                    verts[d.index] = { x: snapValue(mm.x), y: snapValue(mm.y) }
                    return { ...prev, data: { ...prev.data, vertices: verts } }
                })
                return
            }
            if (d.dir === 'segment') {
                // Move the whole segment perpendicular to its own direction:
                // a vertical segment slides horizontally, a horizontal one vertically.
                const ddx = mm.x - d.startMmX, ddy = mm.y - d.startMmY
                const along = ddx * d.ux + ddy * d.uy
                const px = snapValue(ddx - along * d.ux)
                const py = snapValue(ddy - along * d.uy)
                setSelected(prev => {
                    const verts = d.origVerts.map((v, idx) =>
                        (idx === d.index || idx === d.index + 1) ? { x: v.x + px, y: v.y + py } : v)
                    return { ...prev, data: { ...prev.data, vertices: verts } }
                })
                return
            }
            const dx = mm.x - d.startMmX
            const dy = mm.y - d.startMmY
            if (d.dir === 'move') {
                setSelected(prev => ({
                    ...prev,
                    data: { ...prev.data, geometry: { ...prev.data.geometry, x: snapValue(d.origX + dx), y: snapValue(d.origY + dy) } }
                }))
            } else if (d.dir === 'rotate') {
                const g = selected.data.geometry
                // Render applies rotate(-rotation), so negate the screen-space angle
                // to keep the shape following the cursor instead of mirroring it.
                const angle = -(Math.atan2(mm.y - g.y, mm.x - g.x) * 180 / Math.PI + 90)
                setSelected(prev => ({
                    ...prev,
                    data: { ...prev.data, geometry: { ...prev.data.geometry, rotation: Math.round(angle) } }
                }))
            } else {
                // Edge-anchored resize in the shape's LOCAL frame so rotated shapes
                // resize along their own width/height axes (not world X/Y), and the
                // edge opposite the dragged handle stays fixed in world space.
                // Render uses rotate(-θ): worldOffset = M(-θ)·local, so
                // local = M(θ)·world and the centre shift maps back via M(-θ).
                const { origX, origY, origW, origH } = d
                const rad = (d.rotation || 0) * Math.PI / 180
                const cos = Math.cos(rad), sin = Math.sin(rad)
                const ldx = cos * dx - sin * dy
                const ldy = sin * dx + cos * dy
                const sldx = snapValue(ldx), sldy = snapValue(ldy)
                let newW = origW, newH = origH, cx = 0, cy = 0
                if (d.dir.includes('e')) { newW = Math.max(SNAP, origW + sldx); cx = (newW - origW) / 2 }
                if (d.dir.includes('w')) { newW = Math.max(SNAP, origW - sldx); cx = -(newW - origW) / 2 }
                if (d.dir.includes('s')) { newH = Math.max(SNAP, origH + sldy); cy = (newH - origH) / 2 }
                if (d.dir.includes('n')) { newH = Math.max(SNAP, origH - sldy); cy = -(newH - origH) / 2 }
                const worldX = origX + cos * cx + sin * cy
                const worldY = origY - sin * cx + cos * cy
                setSelected(prev => ({
                    ...prev,
                    data: { ...prev.data, geometry: { ...prev.data.geometry, x: worldX, y: worldY, width: newW, height: newH } }
                }))
            }
        } else if (panRef.current) {
            doPan(e)
        }
    }

    async function onPointerUp(e) {
        // A pinch moves the viewport, never the drawing — nothing to save.
        if (gestures.pointerUp(e)) return

        if (objectDrawRef.current) {
            const start = objectDrawRef.current
            objectDrawRef.current = null
            setObjectDraft(null)
            await createObject(start, toMm(e.clientX, e.clientY))
            return
        }
        if (dragRef.current && selected) {
            const d = dragRef.current
            dragRef.current = null
            if (d.dir === 'vertex' || d.dir === 'segment') await savePath(selected.data)
            else await saveGeometry(selected)
        }
        endPan()
    }

    function startBodyDrag(e, item, type) {
        if (mode !== 'select') return
        e.stopPropagation()
        setSelected({ type, id: item.id, data: { ...item } })
        const mm = toMm(e.clientX, e.clientY)
        const g = item.geometry || {}
        dragRef.current = { dir: 'move', startMmX: mm.x, startMmY: mm.y, origX: g.x || 0, origY: g.y || 0, origW: g.width, origH: g.height }
    }

    // Select a work-center position and start moving it. Its geometry is stored as
    // a rack-local offset; while selected we hold it as absolute world geometry so
    // the shared move/resize/rotate handlers apply, converting back on save.
    function startPositionDrag(e, loc, rack) {
        if (mode !== 'select') return
        e.stopPropagation()
        const world = posWorldGeom(loc.geometry, rack.geometry)
        setSelected({ type: 'position', id: loc.id, rackId: rack.id, data: { ...loc, geometry: world } })
        const mm = toMm(e.clientX, e.clientY)
        dragRef.current = { dir: 'move', startMmX: mm.x, startMmY: mm.y, origX: world.x, origY: world.y, origW: world.width, origH: world.height }
    }

    // Re-start a move drag on the already-selected position (its rect in world space).
    function startSelectedGeomMove(e) {
        if (!selected?.data?.geometry) return
        e.stopPropagation()
        const mm = toMm(e.clientX, e.clientY)
        const g = selected.data.geometry
        dragRef.current = { dir: 'move', startMmX: mm.x, startMmY: mm.y, origX: g.x, origY: g.y, origW: g.width, origH: g.height }
    }

    function cleanData(data) {
        const { _unplaced, _isArea, _isObject, _placed, ...rest } = data
        return rest
    }

    async function saveGeometry(sel) {
        if (!sel) return
        const { type, id, data } = sel
        const clean = cleanData(data)
        try {
            if (type === 'hall') {
                await putMe(`warehouse/${warehouseId}/hall`, clean, id)
                refetchHalls()
            } else if (type === 'rack') {
                await putMe('rack', { ...clean, sizeOverride: true }, id)
                refetchRacks()
            } else if (type === 'object') {
                await putMe('warehouseLayoutObject', clean, id)
                refetchObjects()
            } else if (type === 'position') {
                // Convert the edited world geometry back to a footprint-local offset.
                const rackG = racksWithGeometry.find(r => r.id === sel.rackId)?.geometry
                if (!rackG) return
                const off = posLocalGeom(data.geometry, rackG)
                await putMe('location', cleanData({ ...data, geometry: off }), id)
                refetchLocations()
            }
        } catch (_) { /* ignore */ }
    }

    async function savePath(data) {
        try {
            await putMe('warehousePath', cleanData(data), data.id)
            refetchPaths()
        } catch (_) { /* ignore */ }
    }

    async function deleteSelected() {
        if (!selected) return
        const { type, id } = selected
        try {
            let response
            if (type === 'warehousePath') { response = await deleteMe('warehousePath', id); refetchPaths() }
            else if (type === 'hall') { response = await deleteMe(`warehouse/${warehouseId}/hall`, id); refetchHalls() }
            else if (type === 'object') { response = await deleteMe('warehouseLayoutObject', id); refetchObjects() }

            // deleteMe resolves with the error body on a 4xx rather than throwing, so a
            // blocked delete would otherwise look like it worked until the refetch
            // silently put the shape back. Surface the guard's reason instead.
            const guardMessage = toGuardMessage(response)
            if (guardMessage) {
                const body = Array.isArray(guardMessage.message) ? guardMessage.message[0] : guardMessage.message
                showToast(t(guardMessage.title), t(body), guardMessage.type)
                return
            }
        } catch (_) { /* ignore */ }
        setSelected(null)
    }

    // Objects are no-access: a path point inside one, or a segment crossing one,
    // is rejected so paths can't run over objects.
    function segmentHitsObject(a, b) {
        return objectsWithGeometry.some(o => {
            const g = o.geometry
            if (!g) return false
            const x0 = g.x - g.width / 2, x1 = g.x + g.width / 2
            const y0 = g.y - g.height / 2, y1 = g.y + g.height / 2
            if (b.x >= x0 && b.x <= x1 && b.y >= y0 && b.y <= y1) return true
            if (!a) return false
            const c = [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }]
            for (let i = 0; i < 4; i++) {
                if (segIntersect(a, b, c[i], c[(i + 1) % 4])) return true
            }
            return false
        })
    }

    function handleCanvasClick(e) {
        if (mode === 'addPath') {
            const mm = toMm(e.clientX, e.clientY)
            setDrawVerts(prev => {
                const last = prev[prev.length - 1]
                const pt = last ? orthoSnap(last, mm) : { x: snapValue(mm.x), y: snapValue(mm.y) }
                if (segmentHitsObject(last, pt)) return prev  // no-access: ignore
                return [...prev, pt]
            })
        } else {
            setSelected(null)
        }
    }

    function handlePathPointerDown(e, path) {
        if (mode !== 'select') return
        e.stopPropagation()
        const isSelected = selected?.id === path.id
        // Ctrl/Cmd+click on an already-selected path inserts a breakpoint.
        if (isSelected && (e.ctrlKey || e.metaKey)) {
            const mm = toMm(e.clientX, e.clientY)
            const data = { ...selected.data, vertices: insertVertex(selected.data.vertices || [], mm) }
            setSelected(prev => ({ ...prev, data }))
            savePath(data)
            return
        }
        setSelected({ type: 'warehousePath', id: path.id, data: { ...path } })
    }

    async function finishPath(e) {
        if (e) e.stopPropagation()
        if (drawVerts.length < 2) { setDrawVerts([]); return }
        try {
            await postMe('warehousePath', {
                warehouseId, warehouseCode,
                pathType: 'AISLE',
                vertices: drawVerts,
                name: `Path ${new Date().toLocaleTimeString()}`
            })
            refetchPaths()
        } catch (_) { /* ignore */ }
        setDrawVerts([])
        setCursorMm(null)
    }

    function startObjectDraw(e) {
        const mm = toMm(e.clientX, e.clientY)
        objectDrawRef.current = { x0: mm.x, y0: mm.y }
        setObjectDraft({ x0: mm.x, y0: mm.y, x1: mm.x, y1: mm.y })
    }

    async function createObject(start, end) {
        const minX = Math.min(start.x0, end.x), maxX = Math.max(start.x0, end.x)
        const minY = Math.min(start.y0, end.y), maxY = Math.max(start.y0, end.y)
        const width = snapValue(maxX - minX), height = snapValue(maxY - minY)
        if (width < 500 || height < 500) return  // ignore stray clicks
        const name = (window.prompt(t('warehouse.layout.object.namePrompt'), '') || '').trim()
        if (!name) return  // cancelled
        const geometry = {
            x: snapValue((minX + maxX) / 2),
            y: snapValue((minY + maxY) / 2),
            width, height, rotation: 0,
        }
        try {
            await postMe('warehouseLayoutObject', {
                warehouseId,
                warehouseCode,
                name,
                geometry,
            })
            refetchObjects()
        } catch (_) { /* ignore */ }
    }

    function cancelDrawing() {
        setDrawVerts([])
        setCursorMm(null)
        setObjectDraft(null)
        objectDrawRef.current = null
    }

    function switchMode(next) {
        cancelDrawing()
        setMode(next)
    }

    // Reset clears every saved geometry so the auto-placement routine lays
    // everything out from scratch (racks inside their halls, etc.).
    async function resetLayout() {
        if (!window.confirm(t('warehouse.layout.reset.confirm'))) return
        cancelDrawing()
        setSelected(null)
        setMode('select')
        const blank = { x: 0, y: 0, width: 0, height: 0, rotation: 0 }
        try {
            await Promise.all([
                ...racks.filter(r => r.geometry?.width).map(r => putMe('rack', { ...r, geometry: blank, sizeOverride: false }, r.id)),
                ...halls.filter(h => h.geometry?.width).map(h => putMe(`warehouse/${warehouseId}/hall`, { ...h, geometry: blank }, h.id)),
                ...paths.map(p => deleteMe('warehousePath', p.id)),
            ])
        } catch (_) { /* ignore */ }
        await Promise.all([refetchRacks(), refetchHalls(), refetchPaths()])
        didFitRef.current = false
        setFitNonce(n => n + 1)
    }

    async function rebuildRouting() {
        setRebuilding(true)
        try {
            await postMe(`warehouse/${warehouseId}/rebuildRouting`, {})
            const r = await getMe(`warehouseRouteGraph/${warehouseId}`)
            setGraph(r?.data || r)
        } catch (_) { /* ignore */ }
        setRebuilding(false)
    }

    useEffect(() => {
        function onKey(e) {
            if (e.key === 'Escape') {
                if (mode === 'addPath' && drawVerts.length > 1) finishPath()
                else { cancelDrawing(); setMode('select'); setSelected(null) }
            } else if ((e.key === 'Delete' || e.key === 'Backspace') && selected &&
                (selected.type === 'warehousePath' || selected.type === 'hall' || selected.type === 'object')) {
                deleteSelected()
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [mode, drawVerts, selected])

    // Displayed zoom is normalised so a full-warehouse fit reads ~20% (100% is a
    // close-in working zoom), rather than the raw px-per-mm scale (~4%).
    const scaleLabel = `${Math.round(vp.scale / ZOOM_DISPLAY_REF * 100)}%`
    const drawing = mode === 'addPath' || mode === 'addObject'
    const interactionsOff = drawing || mode === 'pan'

    // Junction nodes where paths cross — include the in-progress path while drawing.
    const previewPath = (mode === 'addPath' && drawVerts.length > 0)
        ? [{ id: '__draw__', vertices: [...drawVerts, cursorMm || drawVerts[drawVerts.length - 1]] }]
        : []
    const junctionPoints = computeIntersections([...paths, ...previewPath])

    // Shared renderer for halls and areas (both live in the halls collection).
    function renderHall(h, type = 'hall') {
        return (
            <g
                key={h.id}
                pointerEvents={interactionsOff ? 'none' : undefined}
                onPointerDown={e => startBodyDrag(e, h, type)}
            >
                <HallRect
                    hall={selected && selected.id === h.id ? selected.data : h}
                    selected={selected?.id === h.id}
                    unplaced={h._unplaced}
                    onClick={item => { if (mode === 'select') setSelected({ type, id: item.id, data: { ...item } }) }}
                />
            </g>
        )
    }

    return (
        <Wrap ref={wrapRef} style={canvasSize.height ? { height: canvasSize.height } : undefined}>
            {ToastComponent}
            <Toolbar>
                <ToolBtn $active={mode === 'select'} onClick={() => switchMode('select')}
                    icon={Icon_Pointer} label={t('warehouse.layout.mode.select')} />
                <ToolBtn $active={mode === 'pan'} onClick={() => switchMode('pan')}
                    icon={Icon_Move} label={t('warehouse.layout.mode.pan')} />
                <ToolBtn $active={mode === 'addPath'} onClick={() => switchMode('addPath')}
                    icon={Icon_Route} label={t('warehouse.layout.mode.addPath')} />
                <ToolBtn $active={mode === 'addObject'} onClick={() => switchMode('addObject')}
                    icon={Icon_Rectangle} label={t('warehouse.layout.mode.addObject')} />
                {mode === 'addPath' && drawVerts.length > 1 && (
                    <ToolBtn onClick={finishPath}
                        icon={Icon_CheckMark} label={t('warehouse.layout.path.finish')} />
                )}
                <ToolBtn onClick={zoomToFit}
                    icon={Icon_Fit} label={t('warehouse.layout.zoom.fit')} />
                <ToolBtn onClick={resetLayout} desktopOnly
                    icon={Icon_Reset} label={t('warehouse.layout.zoom.reset')} />
                <ZoomControl>
                    <ZoomBtn onClick={() => zoomBy(0.8)}>−</ZoomBtn>
                    <ZoomLabel>{scaleLabel}</ZoomLabel>
                    <ZoomBtn onClick={() => zoomBy(1.25)}>+</ZoomBtn>
                </ZoomControl>
                <ToolBtn onClick={rebuildRouting} disabled={rebuilding}
                    icon={Icon_Sync} label={t('warehouse.layout.rebuild')}
                    text={rebuilding ? '…' : undefined} />
            </Toolbar>

            <svg
                ref={svgRef}
                width={canvasSize.width || undefined}
                height={canvasSize.height || undefined}
                style={{
                    display: 'block',
                    background: theme.page.background,
                    cursor: drawing ? 'crosshair' : 'grab',
                    // Without this the browser claims every touch for page
                    // scroll and pinch, and none of the handlers below ever run.
                    touchAction: 'none',
                }}
                onPointerDown={e => {
                    if (e.button !== 0) return
                    if (gestures.pointerDown(e)) return  // second finger: pinch, not pan
                    if (mode === 'addObject') { startObjectDraw(e); return }
                    if (mode === 'pan') { startPan(e); return }  // pan only, never select
                    startPan(e); handleCanvasClick(e)
                }}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDoubleClick={mode === 'addPath' ? finishPath : undefined}
            >
                <defs>
                    <pattern id="objectHatch" patternUnits="userSpaceOnUse" width="600" height="600" patternTransform="rotate(45)">
                        <rect width="600" height="600" fill={OBJECT_COLOR} fillOpacity="0.18" />
                        <line x1="0" y1="0" x2="0" y2="600" stroke={OBJECT_COLOR} strokeWidth="160" strokeOpacity="0.6" />
                    </pattern>
                </defs>
                <g transform={`translate(${vp.tx} ${vp.ty}) scale(${vp.scale})`}>
                    {/* halls behind everything; labels in a later pass so they
                        always sit above sibling hall shapes */}
                    {hallsWithGeometry.map(h => renderHall(h, 'hall'))}
                    {hallsWithGeometry.map(h => (
                        <HallLabel key={`lbl-${h.id}`} hall={selected && selected.id === h.id ? selected.data : h} />
                    ))}

                    {/* sub-areas: on top of halls, behind racks */}
                    {areasWithGeometry.map(h => renderHall(h, 'hall'))}
                    {areasWithGeometry.map(h => (
                        <HallLabel key={`lbl-${h.id}`} hall={selected && selected.id === h.id ? selected.data : h} />
                    ))}

                    {/* objects (no-access): on top of halls/areas, behind racks */}
                    {objectsWithGeometry.map(h => renderHall(h, 'object'))}
                    {objectsWithGeometry.map(h => (
                        <HallLabel key={`lbl-${h.id}`} hall={selected && selected.id === h.id ? selected.data : h} />
                    ))}

                    {paths.map(p => (
                        <g key={p.id} pointerEvents={interactionsOff ? 'none' : undefined} onPointerDown={e => handlePathPointerDown(e, p)}>
                            <PathLine
                                path={selected && selected.id === p.id ? selected.data : p}
                                selected={selected?.id === p.id}
                            />
                        </g>
                    ))}

                    <JunctionNodes points={junctionPoints} scale={vp.scale} />

                    {racksWithGeometry.map(r => {
                        const liveRack = selected && selected.id === r.id ? selected.data : r
                        // Work-center footprints (WORKCENTER, or migrated to CUSTOM in
                        // 28.06 Phase C) render as a footprint + drawn positions.
                        if (r.kind === 'WORKCENTER' || r.kind === 'CUSTOM') {
                            return (
                                <g key={r.id} pointerEvents={interactionsOff ? 'none' : undefined} onPointerDown={e => startBodyDrag(e, r, 'rack')}>
                                    <WorkCenterShape
                                        rack={liveRack}
                                        positions={locsByRack[r.id] || []}
                                        selectedPositionId={selected?.type === 'position' ? selected.id : null}
                                        selected={selected?.id === r.id}
                                        onFootprintClick={rack => { if (mode === 'select') setSelected({ type: 'rack', id: rack.id, data: { ...rack } }) }}
                                        onPositionDown={startPositionDrag}
                                    />
                                </g>
                            )
                        }
                        return (
                            <g key={r.id} pointerEvents={interactionsOff ? 'none' : undefined} onPointerDown={e => startBodyDrag(e, r, 'rack')}>
                                <RackShape
                                    rack={liveRack}
                                    selected={selected?.id === r.id}
                                    unplaced={r._unplaced}
                                    onClick={rack => { if (mode === 'select') setSelected({ type: 'rack', id: rack.id, data: { ...rack } }) }}
                                />
                            </g>
                        )
                    })}

                    {graph?.stops && <StopCircles stops={graph.stops} />}

                    {selected && (selected.type === 'rack' || selected.type === 'hall' || selected.type === 'object') && selected.data.geometry && (
                        <SelectionHandles g={selected.data.geometry} scale={vp.scale} onDragStart={startDragHandle} />
                    )}

                    {/* selected work-center position: drawn in world space with handles */}
                    {selected && selected.type === 'position' && selected.data.geometry && (
                        <>
                            <PositionRect
                                cx={selected.data.geometry.x}
                                cy={selected.data.geometry.y}
                                w={selected.data.geometry.width}
                                h={selected.data.geometry.height}
                                rot={selected.data.geometry.rotation || 0}
                                label={selected.data.position}
                                selected
                                onPointerDown={startSelectedGeomMove}
                            />
                            <SelectionHandles g={selected.data.geometry} scale={vp.scale} onDragStart={startDragHandle} />
                        </>
                    )}

                    {selected && selected.type === 'warehousePath' && (
                        <PathHandles path={selected.data} scale={vp.scale} onVertexDown={startVertexDrag} onSegmentDown={startSegmentDrag} />
                    )}

                    {mode === 'addPath' && drawVerts.length > 0 && (() => {
                        const u = 1 / vp.scale
                        const previewPts = [...drawVerts, cursorMm || drawVerts[drawVerts.length - 1]]
                        return (
                            <>
                                <polyline
                                    points={previewPts.map(v => `${v.x},${v.y}`).join(' ')}
                                    fill="none"
                                    stroke={theme.btn.primary}
                                    strokeWidth={2 * u}
                                    strokeDasharray={`${8 * u} ${5 * u}`}
                                    strokeLinecap="round"
                                    opacity={0.9}
                                />
                                {drawVerts.map((v, i) => (
                                    <circle key={i} cx={v.x} cy={v.y} r={5 * u} fill={theme.btn.primary} />
                                ))}
                                {cursorMm && <circle cx={cursorMm.x} cy={cursorMm.y} r={4 * u} fill="none" stroke={theme.btn.primary} strokeWidth={1.5 * u} />}
                            </>
                        )
                    })()}

                    {mode === 'addObject' && objectDraft && (
                        <rect
                            x={Math.min(objectDraft.x0, objectDraft.x1)}
                            y={Math.min(objectDraft.y0, objectDraft.y1)}
                            width={Math.abs(objectDraft.x1 - objectDraft.x0)}
                            height={Math.abs(objectDraft.y1 - objectDraft.y0)}
                            fill={OBJECT_COLOR}
                            fillOpacity={0.25}
                            stroke={OBJECT_COLOR}
                            strokeWidth={(1 / vp.scale) * 2}
                        />
                    )}
                </g>
            </svg>

            {selected && (
                <SelectionPanel>
                    <PanelLabel>
                        <small>{selected.type === 'warehousePath' ? t('warehouse.layout.panel.path')
                            : selected.type === 'object' ? t('warehouse.layout.panel.object')
                            : selected.type === 'position' ? t('warehouse.layout.panel.position')
                            : selected.type === 'hall' && selected.data.isArea ? t('warehouse.layout.panel.area')
                            : selected.type === 'hall' ? t('warehouse.layout.panel.hall')
                            : selected.data.kind === 'WORKCENTER' ? t('warehouse.layout.panel.workCenter')
                            : t('warehouse.layout.panel.rack')}</small>
                        <b>{selected.data.name || selected.data.code || selected.data.position || '—'}</b>
                    </PanelLabel>
                    {(selected.type === 'warehousePath' || selected.type === 'hall' || selected.type === 'object') && (
                        <DangerBtn onClick={deleteSelected}>{t('warehouse.layout.panel.delete')}</DangerBtn>
                    )}
                    <Btn onClick={() => setSelected(null)}>{t('warehouse.layout.panel.deselect')}</Btn>
                </SelectionPanel>
            )}

            {mode === 'addPath' && <Hint>{t('warehouse.layout.hint.addPath')}</Hint>}
            {mode === 'addObject' && <Hint>{t('warehouse.layout.hint.addObject')}</Hint>}
            {!drawing && selected?.type === 'warehousePath' && <Hint>{t('warehouse.layout.hint.editPath')}</Hint>}
        </Wrap>
    )
}

export default WarehouseLayoutDesigner
